import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { CONTACT } from "@/lib/siteData";
import { BRAND } from "@/lib/brand";
import { LANDING } from "@/constants/testIds";
import { BrandLogo } from "@/components/BrandLogo";

export const Footer = ({ partnerSlug = null }) => {
  // When rendered on a partner storefront, keep the partner attribution
  // on the footer Book link so a booking started from the footer still
  // credits the partner (and gets partner pricing).
  const bookTo = partnerSlug
    ? `/book?p=${encodeURIComponent(partnerSlug)}`
    : "/book-water-test";
  return (
    <footer data-testid={LANDING.footer} className="relative bg-slate-950 text-slate-300 pt-20 pb-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 pb-14 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <BrandLogo to="/" variant="dark" size="md" />
            <p className="mt-5 text-base text-slate-400 max-w-md leading-relaxed">
              Australia-wide water testing laboratory. Professional analysis. Plain-English answers. Practical recommendations.
            </p>
            {BRAND.formerly && (
              <p data-testid="footer-brand-formerly" className="mt-3 text-xs text-slate-500 max-w-md leading-relaxed">
                {BRAND.name}, {BRAND.formerly}.
              </p>
            )}
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400 mb-4">Contact</div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-cyan-500 flex-shrink-0" />
                <span className="text-sm text-slate-300">{CONTACT.address}</span>
              </div>
              <a href={CONTACT.phoneLink} className="flex items-center gap-3 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                <span className="text-sm">{CONTACT.phone}</span>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                <span className="text-sm break-all">{CONTACT.email}</span>
              </a>
              {/* Aware Feb-2026 — footer "Check our service area"
                  modal removed. Aware operates nationally now, so we
                  no longer expose the suburb / postcode lookup. */}
            </div>
          </div>

          {/* Water Tests links */}
          <div className="lg:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400 mb-4">Water Tests</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/aware-4-point" data-testid="footer-link-basic" className="hover:text-white transition-colors">AWARE 4-Point</Link></li>
              <li><Link to="/aware-tank-360" data-testid="footer-link-tank" className="hover:text-white transition-colors">AWARE Tank 360</Link></li>
              <li><Link to="/aware-bore-360" data-testid="footer-link-bore" className="hover:text-white transition-colors">AWARE Bore 360</Link></li>
              <li><Link to="/bore-water-testing" data-testid="footer-link-bore-water-testing" className="hover:text-white transition-colors">Bore Water Testing</Link></li>
              <li><Link to={bookTo} data-testid="footer-link-book" className="hover:text-white transition-colors font-semibold">Book a Water Test →</Link></li>
              <li><Link to="/my-water" data-testid="footer-link-my-water" className="hover:text-white transition-colors">My Water Portal</Link></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400 mb-4">Quick Links</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/property-managers" data-testid="footer-link-pm" className="hover:text-white transition-colors">Property Managers</Link></li>
              <li><Link to="/locations" data-testid="footer-link-areas" className="hover:text-white transition-colors">Popular Locations</Link></li>
              <li><Link to="/find-a-partner" data-testid="footer-link-find-partner" className="hover:text-white transition-colors">Find a Partner</Link></li>
              <li><Link to="/knowledge-hub" data-testid="footer-link-learn" className="hover:text-white transition-colors">Learn</Link></li>
              <li><Link to="/faqs" data-testid="footer-link-faqs" className="hover:text-white transition-colors">FAQs</Link></li>
              <li><Link to="/blog" data-testid="footer-link-blog" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-white transition-colors">Contact</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/customer/login" className="hover:text-white transition-colors">Customer Account</Link></li>
              <li><Link to="/track" className="hover:text-white transition-colors">Track Sample</Link></li>
              <li><Link to="/trade/login" className="hover:text-white transition-colors">Trade Portal</Link></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400 mb-3">Important information</div>
          <p className="text-xs text-slate-400 leading-relaxed max-w-4xl">
            {BRAND.name}{BRAND.formerly ? ` (${BRAND.formerly})` : ""} is an independent water testing laboratory. Testing services are provided for informational and water quality assessment purposes. Where regulatory, legal, council, food production, compliance or certification requirements apply, clients should confirm whether accredited laboratory testing is required for their specific application. All test results relate solely to the sample as received by the laboratory.
          </p>
        </div>

        <div className="pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
            {" · "}
            <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white transition-colors underline-offset-2 hover:underline">Privacy Policy</Link>
            {" · "}
            <Link to="/terms" data-testid="footer-link-terms" className="hover:text-white transition-colors underline-offset-2 hover:underline">Terms of Service</Link>
          </p>
          <p className="text-xs text-slate-500">
            {BRAND.formerly
              ? `${BRAND.formerly.charAt(0).toUpperCase()}${BRAND.formerly.slice(1)} · Noosaville, QLD`
              : "Noosaville, QLD"}
          </p>
        </div>
      </div>
    </footer>
  );
};
